import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'
import { Workbook } from 'xlsx'
import * as XLSX from 'xlsx'
import dayjs from 'dayjs'
import { getJalaliYearMonth } from './calendar'

interface FinanceItem {
  id: string
  title: string
  amount: number
  type: 'income' | 'expense'
  category: string
  date: string
}

interface ReportData {
  month: string
  year: number
  jalaliMonth?: number
  jalaliYear?: number
  income: number
  expense: number
  balance: number
  incomeByCategoryData: Array<{ name: string; value: number }>
  expenseByCategoryData: Array<{ name: string; value: number }>
  items: FinanceItem[]
  calendar: 'jalali' | 'gregorian'
}

/**
 * Export monthly finance report as PNG screenshot
 */
export async function exportFinancePNG(
  reportElement: HTMLElement
): Promise<void> {
  try {
    const canvas = await html2canvas(reportElement, {
      scale: 2,
      backgroundColor: '#ffffff',
      logging: false,
      allowTaint: true,
      useCORS: true,
    })

    const link = document.createElement('a')
    link.href = canvas.toDataURL('image/png')
    link.download = `Finance_Report_${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.png`
    link.click()
  } catch (error) {
    console.error('Error generating PNG report:', error)
    alert('خطا در ایجاد گزارش')
  }
}

/**
 * Export monthly finance report as PDF (Vertical/Portrait)
 */
export async function exportFinancePDF(
  reportData: ReportData,
  chartsElement?: HTMLElement
): Promise<void> {
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  })

  const pageWidth = pdf.internal.pageSize.getWidth()
  const pageHeight = pdf.internal.pageSize.getHeight()
  let yPosition = 15

  // Title
  pdf.setFontSize(20)
  pdf.setFont('', 'bold')
  pdf.text('گزارش مالی ماهانه', pageWidth / 2, yPosition, { align: 'center' })
  yPosition += 12

  // Month and Year
  pdf.setFontSize(12)
  pdf.setFont('', 'normal')
  pdf.text(`${reportData.month} - ${reportData.year}`, pageWidth / 2, yPosition, { align: 'center' })
  yPosition += 15

  // Stats Section
  pdf.setFontSize(11)
  pdf.setFont('', 'bold')
  pdf.text('خلاصه آماری:', 15, yPosition)
  yPosition += 8

  // Stats in table format
  const statsData = [
    ['درآمد کل', `${Math.round(reportData.income).toLocaleString('fa-IR')}`],
    ['هزینه کل', `${Math.round(reportData.expense).toLocaleString('fa-IR')}`],
    ['موجودی', `${Math.round(reportData.balance).toLocaleString('fa-IR')}`],
  ]

  pdf.setFont('', 'normal')
  pdf.setFontSize(10)
  let tableY = yPosition
  statsData.forEach(([label, value]) => {
    pdf.text(label, 15, tableY, { align: 'right' })
    pdf.text(value, pageWidth - 15, tableY, { align: 'left' })
    tableY += 7
  })
  yPosition = tableY + 5

  // Income by Category
  if (reportData.incomeByCategoryData.length > 0) {
    pdf.setFont('', 'bold')
    pdf.setFontSize(11)
    pdf.text('درآمد بر اساس دسته:', 15, yPosition)
    yPosition += 8

    pdf.setFont('', 'normal')
    pdf.setFontSize(9)
    reportData.incomeByCategoryData.forEach(({ name, value }) => {
      if (yPosition > pageHeight - 20) {
        pdf.addPage()
        yPosition = 15
      }
      pdf.text(`${name}: ${value.toLocaleString('fa-IR')}`, 20, yPosition)
      yPosition += 6
    })
    yPosition += 3
  }

  // Expense by Category
  if (reportData.expenseByCategoryData.length > 0) {
    pdf.setFont('', 'bold')
    pdf.setFontSize(11)
    pdf.text('هزینه بر اساس دسته:', 15, yPosition)
    yPosition += 8

    pdf.setFont('', 'normal')
    pdf.setFontSize(9)
    reportData.expenseByCategoryData.forEach(({ name, value }) => {
      if (yPosition > pageHeight - 20) {
        pdf.addPage()
        yPosition = 15
      }
      pdf.text(`${name}: ${value.toLocaleString('fa-IR')}`, 20, yPosition)
      yPosition += 6
    })
    yPosition += 5
  }

  // Transactions table
  if (reportData.items.length > 0) {
    if (yPosition > pageHeight - 40) {
      pdf.addPage()
      yPosition = 15
    }

    pdf.setFont('', 'bold')
    pdf.setFontSize(11)
    pdf.text('تراکنش‌های ماه:', 15, yPosition)
    yPosition += 8

    // Table header
    pdf.setFont('', 'bold')
    pdf.setFontSize(9)
    const headerY = yPosition
    pdf.text('نوع', 15, headerY)
    pdf.text('دسته', 40, headerY)
    pdf.text('عنوان', 80, headerY)
    pdf.text('مبلغ', 140, headerY)
    pdf.text('تاریخ', 170, headerY)
    yPosition += 6

    // Draw horizontal line
    pdf.line(15, headerY + 1, pageWidth - 15, headerY + 1)
    yPosition += 2

    // Table rows
    pdf.setFont('', 'normal')
    pdf.setFontSize(8)
    reportData.items.forEach((item) => {
      if (yPosition > pageHeight - 15) {
        pdf.addPage()
        yPosition = 15
      }
      const type = item.type === 'income' ? 'درآمد' : 'هزینه'
      const amount = `${item.type === 'income' ? '+' : '-'}${Math.round(item.amount).toLocaleString('fa-IR')}`
      const dateStr = dayjs(item.date).format('YYYY-MM-DD')

      pdf.text(type, 15, yPosition)
      pdf.text(item.category, 40, yPosition)
      pdf.text(item.title, 80, yPosition)
      pdf.text(amount, 140, yPosition)
      pdf.text(dateStr, 170, yPosition)
      yPosition += 6
    })
  }

  // Footer
  pdf.setFontSize(8)
  pdf.setFont('', 'normal')
  pdf.setTextColor(128, 128, 128)
  const generatedDate = dayjs().format('YYYY-MM-DD HH:mm')
  pdf.text(
    `تاریخ تولید: ${generatedDate}`,
    pageWidth / 2,
    pageHeight - 10,
    { align: 'center' }
  )

  pdf.save(`Finance_Report_${reportData.year}-${String(reportData.jalaliMonth || reportData.month).padStart(2, '0')}.pdf`)
}

/**
 * Export monthly finance report as Excel (Landscape)
 */
export function exportFinanceExcel(reportData: ReportData): void {
  const workbook = XLSX.utils.book_new()

  // Sheet 1: Summary
  const summarySheet = XLSX.utils.aoa_to_sheet([
    ['گزارش مالی ماهانه'],
    [`${reportData.month} - ${reportData.year}`],
    [],
    ['خلاصه آماری'],
    ['درآمد کل', Math.round(reportData.income)],
    ['هزینه کل', Math.round(reportData.expense)],
    ['موجودی', Math.round(reportData.balance)],
  ])

  // Format header
  summarySheet['A1'].font = { bold: true, size: 16 }
  summarySheet['A2'].font = { bold: true, size: 12 }
  summarySheet['A4'].font = { bold: true }

  XLSX.utils.book_append_sheet(workbook, summarySheet, 'خلاصه')

  // Sheet 2: Category Analysis
  const categoryData = [
    ['دسته', 'درآمد', 'هزینه'],
    ...reportData.incomeByCategoryData.map(cat => [cat.name, cat.value, '']),
    ...reportData.expenseByCategoryData.map(cat => [cat.name, '', cat.value]),
  ]
  const categorySheet = XLSX.utils.aoa_to_sheet(categoryData)
  categorySheet['A1'].font = { bold: true }
  categorySheet['B1'].font = { bold: true }
  categorySheet['C1'].font = { bold: true }
  XLSX.utils.book_append_sheet(workbook, categorySheet, 'تحلیل دسته‌ها')

  // Sheet 3: Transactions
  const transactionData = [
    ['تاریخ', 'عنوان', 'دسته', 'نوع', 'مبلغ'],
    ...reportData.items.map(item => [
      dayjs(item.date).format('YYYY-MM-DD'),
      item.title,
      item.category,
      item.type === 'income' ? 'درآمد' : 'هزینه',
      item.type === 'income' ? item.amount : -item.amount,
    ]),
  ]
  const transactionSheet = XLSX.utils.aoa_to_sheet(transactionData)
  transactionSheet['A1'].font = { bold: true }
  transactionSheet['B1'].font = { bold: true }
  transactionSheet['C1'].font = { bold: true }
  transactionSheet['D1'].font = { bold: true }
  transactionSheet['E1'].font = { bold: true }

  // Set column widths
  transactionSheet['!cols'] = [
    { wch: 15 }, // Date
    { wch: 25 }, // Title
    { wch: 15 }, // Category
    { wch: 10 }, // Type
    { wch: 15 }, // Amount
  ]

  XLSX.utils.book_append_sheet(workbook, transactionSheet, 'تراکنش‌ها')

  // Save with landscape orientation
  const fileName = `Finance_Report_${reportData.year}-${String(reportData.jalaliMonth || reportData.month).padStart(2, '0')}.xlsx`
  XLSX.writeFile(workbook, fileName)
}
